import { enCatalog, type MessageCatalog } from './catalog/en';
import { esCatalog } from './catalog/es';
import { DEFAULT_LANGUAGE, getFallbackChain } from './languages';

/** Built-in catalogs shipped with the app (en is the English source). */
export const BUILT_IN_CATALOGS: Record<string, MessageCatalog> = {
  en: enCatalog,
  es: esCatalog,
};

const CATALOG_CACHE_PREFIX = 'rc_i18n_catalog_';

/**
 * Translations saved in Translation Admin (GET /i18n/catalog/:code).
 * Per key, a saved translation wins; otherwise the built-in catalog supplies
 * the AI draft that ships with the app.
 */
const remoteCatalogs: Record<string, MessageCatalog> = {};

function readCachedCatalog(code: string): MessageCatalog | null {
  try {
    const raw = localStorage.getItem(CATALOG_CACHE_PREFIX + code);
    return raw ? (JSON.parse(raw) as MessageCatalog) : null;
  } catch {
    return null;
  }
}

export function setRemoteCatalog(code: string, messages: MessageCatalog): void {
  if (!code || code === 'en') return;
  remoteCatalogs[code] = messages;
  try {
    localStorage.setItem(CATALOG_CACHE_PREFIX + code, JSON.stringify(messages));
  } catch {
    /* ignore quota / privacy mode */
  }
}

function lookup(code: string, key: string): string | undefined {
  if (code === 'en') return enCatalog[key];
  if (!(code in remoteCatalogs)) {
    const cached = readCachedCatalog(code);
    if (cached) remoteCatalogs[code] = cached;
  }
  const saved = remoteCatalogs[code]?.[key];
  if (saved !== undefined && saved !== '') return saved;
  return BUILT_IN_CATALOGS[code]?.[key];
}

/** Module-level active locale — set by LocaleProvider so non-React helpers can resolve. */
let activeLocale: string = DEFAULT_LANGUAGE;

export function setActiveLocale(locale: string): void {
  activeLocale = locale || DEFAULT_LANGUAGE;
}

export function getActiveLocale(): string {
  return activeLocale;
}

export type TranslateVars = Record<string, string | number | undefined>;

function applyVars(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    if (v === undefined) continue;
    out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}

/**
 * Resolve a message key with locale → fallback → en.
 * Never returns blank; last resort is the key itself.
 */
export function resolveMessage(
  key: string,
  opts?: { locale?: string; vars?: TranslateVars }
): string {
  const locale = opts?.locale ?? activeLocale;
  const chain = getFallbackChain(locale);

  for (const code of chain) {
    const value = lookup(code, key);
    if (value !== undefined && value !== '') {
      return applyVars(value, opts?.vars);
    }
  }

  return applyVars(key, opts?.vars);
}

/** Convenience alias used by UI helpers. */
export function t(key: string, vars?: TranslateVars): string {
  return resolveMessage(key, { vars });
}

/** Fill {stepName} from the stepName.N catalog entry in the active locale. */
export function tStepTemplate(templateKey: string, stepNumber: number): string {
  const stepName = resolveMessage(`stepName.${stepNumber}`);
  return resolveMessage(templateKey, { vars: { stepName } });
}
